/**
 * Session Storage Keys and Utilities for Interstitial Popups
 *
 * This module manages session-based state for homepage collection and
 * cart abandonment popups to ensure they work in sync.
 *
 * Session storage is used (not localStorage) so that:
 * - State is cleared when browser/tab is closed
 * - Each tab has its own session state
 * - Popups can reappear in new tabs/browsers
 */

export const SESSION_KEYS = {
  // Set to 'true' when user successfully submits form on ANY popup
  DETAILS_SUBMITTED: 'interstitial_details_submitted',
  // Set to 'true' when homepage popup is closed without submitting
  HOMEPAGE_POPUP_CLOSED: 'interstitial_homepage_popup_closed',
  // Set to 'true' when cart popup is closed without submitting
  CART_POPUP_CLOSED: 'interstitial_cart_popup_closed',
  // Set to 'true' when homepage popup has been shown in this session
  HOMEPAGE_POPUP_SHOWN: 'interstitial_homepage_popup_shown',
  // Set to 'true' when the popup is currently open (to persist across page reloads from cookie consent)
  POPUP_CURRENTLY_OPEN: 'interstitial_popup_currently_open',
} as const;

/**
 * Get a boolean value from session storage
 */
export const getSessionValue = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem(key) === 'true';
};

/**
 * Set a boolean value in session storage
 */
export const setSessionValue = (key: string, value: boolean): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(key, String(value));
};

/**
 * Check if user has submitted details on ANY popup in this session
 */
export const hasUserSubmittedDetails = (): boolean => {
  return getSessionValue(SESSION_KEYS.DETAILS_SUBMITTED);
};

/**
 * Check if homepage popup was closed without submitting
 */
export const hasHomepagePopupClosedWithoutSubmit = (): boolean => {
  return getSessionValue(SESSION_KEYS.HOMEPAGE_POPUP_CLOSED);
};

/**
 * Check if cart popup was closed without submitting
 */
export const hasCartPopupClosedWithoutSubmit = (): boolean => {
  return getSessionValue(SESSION_KEYS.CART_POPUP_CLOSED);
};

/**
 * Check if homepage popup has already been shown in this session
 */
export const hasHomepagePopupShownThisSession = (): boolean => {
  return getSessionValue(SESSION_KEYS.HOMEPAGE_POPUP_SHOWN);
};

/**
 * Mark that user has submitted details (on any popup)
 * This prevents BOTH popups from showing again in this session
 */
export const markDetailsSubmitted = (): void => {
  setSessionValue(SESSION_KEYS.DETAILS_SUBMITTED, true);
  // Clear the closed-without-submit flags since user submitted
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEYS.HOMEPAGE_POPUP_CLOSED);
    sessionStorage.removeItem(SESSION_KEYS.CART_POPUP_CLOSED);
  }
};

/**
 * Mark that homepage popup was closed without submitting
 * This allows cart popup to show on exit intent
 */
export const markHomepagePopupClosed = (): void => {
  setSessionValue(SESSION_KEYS.HOMEPAGE_POPUP_CLOSED, true);
  setSessionValue(SESSION_KEYS.HOMEPAGE_POPUP_SHOWN, true);
};

/**
 * Mark that cart popup was closed without submitting
 * This allows homepage popup to show on next page landing
 */
export const markCartPopupClosed = (): void => {
  setSessionValue(SESSION_KEYS.CART_POPUP_CLOSED, true);
};

/**
 * Mark that homepage popup has been shown in this session
 */
export const markHomepagePopupShown = (): void => {
  setSessionValue(SESSION_KEYS.HOMEPAGE_POPUP_SHOWN, true);
};

/**
 * Check if the popup is currently open (to restore after page reload from cookie consent rejection)
 */
export const isPopupCurrentlyOpen = (): boolean => {
  return getSessionValue(SESSION_KEYS.POPUP_CURRENTLY_OPEN);
};

/**
 * Mark that the popup is currently open (to persist across page reload from cookie consent rejection)
 */
export const markPopupOpen = (): void => {
  setSessionValue(SESSION_KEYS.POPUP_CURRENTLY_OPEN, true);
};

/**
 * Mark that the popup is closed (to clear the open state)
 */
export const markPopupClosed = (): void => {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(SESSION_KEYS.POPUP_CURRENTLY_OPEN);
};
